import { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { onAuthChange, getIdToken, initFirebase } from '@/lib/firebase';
import { User as DbUser } from '@shared/schema';

interface AuthContextType {
  firebaseUser: User | null;
  dbUser: DbUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  getAuthToken: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  firebaseUser: null,
  dbUser: null,
  loading: true,
  isAuthenticated: false,
  getAuthToken: async () => null,
  refreshUser: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [dbUser, setDbUser] = useState<DbUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    
    const setupAuth = async () => {
      try {
        // Initialize Firebase first
        await initFirebase();
        setInitialized(true);
        
        // Then set up the auth state listener
        unsubscribe = await onAuthChange(async (user) => {
          setFirebaseUser(user);
          
          if (user) {
            // Fetch user data from our database
            try {
              const token = await user.getIdToken();
              const response = await fetch('/api/auth/user', {
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });
              
              if (response.ok) {
                const userData = await response.json();
                setDbUser(userData);
              } else {
                setDbUser(null);
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
              setDbUser(null);
            }
          } else {
            setDbUser(null);
          }
          
          setLoading(false);
        });
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setLoading(false);
      }
    };
    
    setupAuth();
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const getAuthToken = async () => {
    if (firebaseUser) {
      return await firebaseUser.getIdToken();
    }
    return null;
  };

  const refreshUser = async () => {
    if (firebaseUser) {
      try {
        const token = await firebaseUser.getIdToken();
        const response = await fetch('/api/auth/user', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const userData = await response.json();
          setDbUser(userData);
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ 
      firebaseUser, 
      dbUser, 
      loading: loading || !initialized, 
      isAuthenticated: !!firebaseUser,
      getAuthToken,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => useContext(AuthContext);